import React, { useEffect, useMemo, useState } from "react";
import { useLive } from "@/lib/live";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CurrencyInr, ChartLineUp, Users, Receipt, ArrowClockwise, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { istToday, istDate } from "@/lib/istDate";
import StaffCommissionApprovals from "@/components/StaffCommissionApprovals";

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const todayStr = () => istToday();
const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s.length === 10 ? s + "T00:00:00" : s)
      .toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch (_e) { return s; }
};

export default function CommissionPage() {
  const { user } = useAuth();
  const canUse = user?.role === "admin" || user?.role === "secondary_admin";

  const monthStart = () => {
    const d = new Date();
    return istDate(new Date(d.getFullYear(), d.getMonth(), 1));
  };

  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(todayStr());
  const [userFilter, setUserFilter] = useState("ALL");
  const [data, setData] = useState({ grand_total: 0, order_count: 0, by_user: [], orders: [] });
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useLive(() => { load(); });
  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from: fromDate, to: toDate });
      if (userFilter && userFilter !== "ALL") params.set("user_email", userFilter);
      const { data: d } = await api.get(`/commissions?${params}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load fail hua");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (canUse) load(); /* eslint-disable-next-line */ }, [fromDate, toDate, userFilter, canUse]);

  const quickRanges = useMemo(() => {
    const d = new Date();
    const iso = (dt) => istDate(dt);
    const today = iso(d);
    const weekStart = iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 6));
    const monStart = iso(new Date(d.getFullYear(), d.getMonth(), 1));
    const prevMon = iso(new Date(d.getFullYear(), d.getMonth() - 1, 1));
    const prevMonEnd = iso(new Date(d.getFullYear(), d.getMonth(), 0));
    const yearStart = iso(new Date(d.getFullYear(), 0, 1));
    return [
      { label: "Today", from: today, to: today },
      { label: "This Week", from: weekStart, to: today },
      { label: "This Month", from: monStart, to: today },
      { label: "Last Month", from: prevMon, to: prevMonEnd },
      { label: "This Year", from: yearStart, to: today },
    ];
  }, []);

  const recompute = async () => {
    if (!window.confirm("Date range ke orders ka commission dobara calculate karoge?\n\n(Items master me latest MRP + Company Price se naya calculation hoga. Order ke total/balance pe koi effect nahi.)")) return;
    setRecomputing(true);
    try {
      const { data: r } = await api.post(`/commissions/recompute?from=${fromDate}&to=${toDate}`);
      toast.success(`${r.orders_updated} orders ka commission update ho gaya`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Recompute fail hua");
    } finally { setRecomputing(false); }
  };

  if (!canUse) {
    return <div className="p-10 text-center text-slate-400">Commissions access sirf admin / secondary admin ke liye hai.</div>;
  }

  return (
    <div className="space-y-5" data-testid="commission-page">
      {user?.role === "admin" && <StaffCommissionApprovals onChanged={load} />}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// EARNINGS</div>
          <h1 className="font-heading text-3xl font-black text-white">Staff Commissions</h1>
          <p className="text-slate-400 text-sm mt-1">
            Har order me staff ne jo MRP se Company Price ke upar bech, utna commission jama hota hai.
            Items master me <b className="text-amber-300">MRP</b> + <b className="text-amber-300">Company Price</b> set karo tabhi count hoga.
          </p>
        </div>
        <Button onClick={recompute} disabled={recomputing}
          variant="outline"
          className="rounded-sm h-10 border-amber-500/40 text-amber-200 hover:bg-amber-500/10 disabled:opacity-60"
          data-testid="commission-recompute-button"
          title="Dobara calculate karein — MRP/CP update karne ke baad useful"
        >
          <ArrowClockwise size={16} weight="bold" className="mr-2" />
          {recomputing ? "Recomputing…" : "Recompute Range"}
        </Button>
      </div>

      {/* Date range */}
      <div className="rounded-sm border border-slate-800 bg-slate-900/40 p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[140px]">
          <Label className="text-xs text-slate-400 tracking-widest">FROM</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="rounded-sm bg-slate-950 border-slate-700 h-10 font-mono-num"
            data-testid="commission-from"/>
        </div>
        <div className="flex-1 min-w-[140px]">
          <Label className="text-xs text-slate-400 tracking-widest">TO</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="rounded-sm bg-slate-950 border-slate-700 h-10 font-mono-num"
            data-testid="commission-to"/>
        </div>
        <div className="flex flex-wrap gap-2">
          {quickRanges.map((r) => (
            <Button key={r.label} variant="outline" size="sm"
              onClick={() => { setFromDate(r.from); setToDate(r.to); }}
              className="rounded-sm h-9 border-slate-700 text-slate-300 hover:bg-slate-800 text-xs tracking-wider"
              data-testid={`commission-range-${r.label.toLowerCase().replace(/\s+/g, '-')}`}>
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-sm border border-amber-500/40 bg-amber-500/5 p-5">
          <div className="flex items-center gap-2">
            <CurrencyInr size={16} className="text-amber-300" />
            <div className="text-xs tracking-widest text-amber-300 font-mono-num">TOTAL COMMISSION</div>
          </div>
          <div className="font-mono-num text-3xl font-black text-amber-200 mt-2" data-testid="commission-grand-total">
            {inr(data.grand_total)}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {fmtDate(fromDate)} — {fmtDate(toDate)} · {data.order_count} orders
          </div>
        </div>
        <div className="rounded-sm border border-slate-800 bg-slate-900/50 p-5 md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} className="text-slate-400" />
            <div className="text-xs tracking-widest text-slate-400 font-mono-num">BY STAFF</div>
          </div>
          {(data.by_user || []).length === 0 ? (
            <div className="text-slate-500 text-sm py-2">Is period mein koi commission earn nahi hua.</div>
          ) : (
            <div className="space-y-2">
              {data.by_user.map((u) => (
                <div key={u.user_email}
                  className="flex items-center justify-between rounded-sm bg-slate-950/60 border border-slate-800 px-3 py-2"
                  data-testid={`staff-row-${u.user_email}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white truncate">{u.user_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{u.user_email} · {u.order_count} orders</div>
                  </div>
                  <div className="font-mono-num text-lg font-black text-amber-200 shrink-0 ml-3">{inr(u.commission)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Staff filter */}
      {(data.by_user || []).length > 1 && (
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-400 tracking-widest">FILTER:</Label>
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-64 rounded-sm bg-slate-900 border-slate-700 h-9" data-testid="commission-user-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-sm bg-slate-900 border-slate-700">
              <SelectItem value="ALL">All Staff</SelectItem>
              {data.by_user.map((u) => (
                <SelectItem key={u.user_email} value={u.user_email}>
                  {u.user_name} ({inr(u.commission)})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Orders list */}
      <div className="rounded-sm border border-slate-800 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-mono-num tracking-widest text-slate-400 bg-slate-900/60 border-b border-slate-800">
          <div className="col-span-2">DATE</div>
          <div className="col-span-2">ORDER #</div>
          <div className="col-span-3">CUSTOMER</div>
          <div className="col-span-2">EARNED BY</div>
          <div className="col-span-1 text-right">TOTAL</div>
          <div className="col-span-2 text-right">COMMISSION</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-400 font-mono-num text-sm">Loading…</div>
        ) : data.orders.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt size={40} className="mx-auto text-slate-600 mb-2" />
            <div className="text-slate-400 text-sm max-w-md mx-auto">
              Is period mein commission wale orders nahi hain. Items master me <b className="text-amber-300">MRP</b> + <b className="text-amber-300">Company Price</b> set karo,
              phir naye orders apne-aap count honge. Purane orders ke liye upar wala <b className="text-amber-300">Recompute</b> button use karo.
            </div>
          </div>
        ) : (
          data.orders.map((o) => {
            const expanded = expandedId === o.order_id;
            return (
              <div key={o.order_id} className="border-b border-slate-800 last:border-b-0"
                data-testid={`commission-order-${o.order_number}`}>
                <button
                  onClick={() => setExpandedId(expanded ? null : o.order_id)}
                  className="w-full grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm hover:bg-slate-900/40 text-left"
                >
                  <div className="col-span-2 flex items-center gap-1 font-mono-num text-slate-200">
                    {expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                    {fmtDate(o.created_at)}
                  </div>
                  <div className="col-span-2 font-mono-num text-slate-100">#{o.order_number}</div>
                  <div className="col-span-3 text-slate-200 truncate">{o.customer_name}</div>
                  <div className="col-span-2 text-slate-400 text-xs truncate">{o.created_by_name}</div>
                  <div className="col-span-1 text-right font-mono-num text-slate-300">{inr(o.total_amount)}</div>
                  <div className="col-span-2 text-right font-mono-num text-amber-200 font-black">{inr(o.total_commission)}</div>
                </button>
                {expanded && (
                  <div className="bg-slate-950/60 border-t border-slate-800 px-4 py-3">
                    <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-2">LINE-BY-LINE</div>
                    <div className="space-y-1.5">
                      {o.items.map((it, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 text-xs">
                          <div className="col-span-5 text-slate-200">{it.item_name}</div>
                          <div className="col-span-2 font-mono-num text-slate-400 text-right">
                            Rate ₹{it.rate}
                          </div>
                          <div className="col-span-2 font-mono-num text-slate-500 text-right">
                            MRP ₹{it.mrp}
                          </div>
                          <div className="col-span-1 font-mono-num text-slate-500 text-right">
                            CP ₹{it.company_price}
                          </div>
                          <div className="col-span-2 font-mono-num text-amber-200 font-bold text-right">
                            = {inr(it.commission_amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
